import { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { isAfter } from 'date-fns/isAfter';

import { ModalV2, ButtonV2, DatePicker } from '@bloomays-lib/ui.shared';
import type {
  ExtensionAssessmentGQL,
  GetExtensionAssessmentGQLResult,
  IMissionCardDetails,
  IMissionCardPricing,
} from '@bloomays-lib/types.shared';
import { GET_EXTENSION_ASSESSMENT, UPSERT_EXTESION_ASSESSMENT } from '@bloomays-lib/adapter.api-bloomer';
import { dateFormat, getFirstValidPrice, greaterDate } from '@bloomays-lib/utils.shared';

import { cancel, notify } from '../../../../helpers/toastify';
import { getAssessmentKey } from '../../../../helpers/locales';
import { shouldDisableDate } from '../../../molecules/ExtensionAssessment';
import { FieldV2, InputV2, SelectV2, Row2, InfoV2, DividerV2, EstCard } from '../formFields';

export type EstimateModalProps = {
  open: boolean;
  onClose: () => void;
  onCreateAmendment: () => void;
  mission: IMissionCardDetails;
};

const fmtEuro = (value: number): string => `${Math.round(value).toLocaleString('fr-FR')} €`;
const fmtDate = (value?: Date | string | null): string => (value ? dateFormat(value, 'dd/MM/yyyy') : '—');

/**
 * Estimer la prolongation (ModalV2) — refonte V2 de `ExtensionAssessment`, enrichie de la projection
 * financière de la maquette. Sauvegarde la probabilité via `UPSERT_EXTESION_ASSESSMENT`.
 *
 * La grille (jours / CA / marge) est pilotée par la « nouvelle date de fin » toujours visible et par
 * le prix actif dérivé de `mission.prices` (même méthode que l'avenant) — pas du prix pré-agrégé.
 */
export const EstimateModal = ({ open, onClose, onCreateAmendment, mission }: EstimateModalProps) => {
  const { t } = useTranslation(['extendShortenMission', 'random', 'notify', 'missionDashboard']);
  const currentEndDate = useMemo(() => new Date(mission.endMission as string), [mission.endMission]);

  const { data } = useQuery<GetExtensionAssessmentGQLResult>(GET_EXTENSION_ASSESSMENT, {
    variables: { missionId: mission.recordId },
    skip: !open,
  });
  const current: ExtensionAssessmentGQL | null = data?.getExtensionAssessment ?? null;
  const [upsertExtensionAssessment, { loading }] = useMutation(UPSERT_EXTESION_ASSESSMENT);

  const [assessment, setAssessment] = useState<number | undefined>(undefined);
  const [newEndDate, setNewEndDate] = useState<Date | null>(null);

  // Prix actif dérivé des prix de la mission (robuste, indépendant de l'agrégat de la page).
  const activePricing = useMemo(
    () =>
      getFirstValidPrice(mission.prices || [], greaterDate(new Date(mission.beginMission as string), new Date())) as
        | IMissionCardPricing
        | undefined,
    [mission.prices, mission.beginMission],
  );
  const sale = activePricing?.salePricePerDayWithoutTaxes ?? mission.salePricePerDayWithoutTaxes ?? 0;
  const purchase = activePricing?.purchasePricePerDayWithoutTaxes ?? mission.purchasePricePerDayWithoutTaxes ?? 0;

  const addDays = newEndDate
    ? Math.max(0, Math.round((newEndDate.getTime() - currentEndDate.getTime()) / 86400000))
    : 0;
  const workDays = Math.round((addDays * 5) / 7);

  const options = [
    { value: 0, label: t('assessmentLabel0') },
    { value: 0.25, label: t('assessmentLabel1') },
    { value: 0.5, label: t('assessmentLabel2') },
    { value: 0.75, label: t('assessmentLabel3') },
    { value: 1, label: t('assessmentLabel4') },
  ];

  // Probabilité haute (> 50 %) ⇒ une vraie date de fin est requise ; sinon on enregistre « aujourd'hui ».
  const requiresFutureDate = assessment !== undefined && assessment > 0.5;
  const futureDateOk = newEndDate !== null && isAfter(newEndDate, currentEndDate);
  const canSave = assessment !== undefined && (!requiresFutureDate || futureDateOk);

  const save = async () => {
    if (assessment === undefined) return;
    const endToSave = requiresFutureDate ? newEndDate : new Date();
    if (!endToSave) return;
    const info = notify('info', t('updateInProgress', { ns: 'notify' }), null, {
      hideProgressBar: false,
      autoClose: 1000,
    });
    try {
      await upsertExtensionAssessment({
        variables: {
          input: { missionId: mission.recordId, assessment, newEndDate: dateFormat(endToSave, 'yyyy-MM-dd') },
        },
        refetchQueries: [{ query: GET_EXTENSION_ASSESSMENT, variables: { missionId: mission.recordId } }],
      });
      notify('success', t('updateSuccess', { ns: 'notify' }), null, { hideProgressBar: false, autoClose: 1000 });
      onClose();
    } catch (e: any) {
      cancel(info);
      notify('error', e.message);
    }
  };

  return (
    <ModalV2
      open={open}
      onClose={onClose}
      icon={<InsightsRoundedIcon sx={{ fontSize: 18 }} />}
      iconTone="bleu"
      title={t('actions.estimateExtension', { ns: 'missionDashboard' })}
      width={560}
      footer={
        <>
          <ButtonV2 variant="secondary" onClick={onClose}>
            {t('close', { ns: 'random' })}
          </ButtonV2>
          <ButtonV2 onClick={save} disabled={!canSave} loading={loading}>
            {t('save', { ns: 'random' })}
          </ButtonV2>
        </>
      }
    >
      {current ? (
        <InfoV2 tone="blue" icon={<InsightsRoundedIcon sx={{ fontSize: 16 }} />}>
          {t(getAssessmentKey(current.assessment))}
          {current.newEndDate ? ` · ${fmtDate(current.newEndDate)}` : ''}
        </InfoV2>
      ) : null}

      <InfoV2 tone="blue" icon={<InsightsRoundedIcon sx={{ fontSize: 16 }} />}>
        {t('actionModals.estimate.infoBefore', { ns: 'missionDashboard' })}{' '}
        <Box
          component="span"
          role="button"
          tabIndex={0}
          onClick={onCreateAmendment}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') onCreateAmendment();
          }}
          sx={{ fontWeight: 700, cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: '2px' }}
        >
          {t('actionModals.estimate.createAmendmentLink', { ns: 'missionDashboard' })}
        </Box>
      </InfoV2>

      <FieldV2 label={t('assesmentInputLabel')} required>
        <SelectV2
          value={assessment ?? ''}
          placeholder={t('drawers.common.choose', { ns: 'missionDashboard' })}
          options={options}
          onChange={(raw) => setAssessment(Number(raw))}
        />
      </FieldV2>

      <Row2>
        <FieldV2 label={t('actionModals.estimate.currentEnd', { ns: 'missionDashboard' })}>
          <InputV2 value={fmtDate(currentEndDate)} disabled readOnly />
        </FieldV2>
        <FieldV2
          label={t('newDate')}
          required={requiresFutureDate}
          error={
            requiresFutureDate && newEndDate && !futureDateOk
              ? t('dateErrorMinEndDate', { minDate: fmtDate(currentEndDate) })
              : undefined
          }
        >
          <DatePicker
            shouldDisableDate={shouldDisableDate(currentEndDate)}
            minDate={currentEndDate}
            label={t('pickDate')}
            value={newEndDate}
            handleChange={setNewEndDate}
          />
        </FieldV2>
      </Row2>

      <DividerV2 />
      <Row2>
        <EstCard label={t('actionModals.estimate.calendarDays', { ns: 'missionDashboard' })} value={`+${addDays}`} />
        <EstCard label={t('actionModals.estimate.workedDays', { ns: 'missionDashboard' })} value={`+${workDays}`} />
        <EstCard
          label={t('actionModals.estimate.additionalRevenue', { ns: 'missionDashboard' })}
          value={fmtEuro(workDays * sale)}
          tone="bleu"
        />
        <EstCard
          label={t('actionModals.estimate.additionalMargin', { ns: 'missionDashboard' })}
          value={fmtEuro(workDays * (sale - purchase))}
          tone="vert"
        />
      </Row2>
    </ModalV2>
  );
};

export default EstimateModal;
